export type OrderSortMode = "default" | "schedule" | "arrival-desc" | "shipping-request-desc";

export interface SortableDashboardOrder {
  requestedDeliveryDate: string;
  wearingDate: string;
  itemArrivalDate: string;
  repairInstockDate?: string;
  shippingRequestDate: string;
  isExpress: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1_000;
const CALENDAR_DATE_PATTERNS = [
  /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
  /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/,
  /^(\d{4})年(\d{1,2})月(\d{1,2})日$/,
];

interface DateDistance {
  absoluteDays: number;
  signedDays: number;
}

function calendarDateMatch(value: string): RegExpExecArray | null {
  const normalized = value.normalize("NFKC").trim();
  for (const pattern of CALENDAR_DATE_PATTERNS) {
    const match = pattern.exec(normalized);
    if (match) return match;
  }
  return null;
}

function dateDistance(value: string, today: Date): DateDistance {
  const match = calendarDateMatch(value);
  if (!match) {
    return { absoluteDays: Number.POSITIVE_INFINITY, signedDays: Number.POSITIVE_INFINITY };
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const dateUtc = Date.UTC(year, month - 1, day);
  const parsed = new Date(dateUtc);
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return { absoluteDays: Number.POSITIVE_INFINITY, signedDays: Number.POSITIVE_INFINITY };
  }

  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const signedDays = Math.round((dateUtc - todayUtc) / DAY_MS);
  return { absoluteDays: Math.abs(signedDays), signedDays };
}

function compareDistance(left: DateDistance, right: DateDistance): number {
  if (left.absoluteDays !== right.absoluteDays) return left.absoluteDays < right.absoluteDays ? -1 : 1;
  if (left.signedDays !== right.signedDays) return left.signedDays < right.signedDays ? -1 : 1;
  return 0;
}

function calendarDateEpoch(value: string): number | null {
  const match = calendarDateMatch(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const epoch = Date.UTC(year, month - 1, day);
  const parsed = new Date(epoch);
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) return null;
  return epoch;
}

function compareLatestDate(leftValue: string, rightValue: string): number {
  const left = calendarDateEpoch(leftValue);
  const right = calendarDateEpoch(rightValue);
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  return right - left;
}

export function defaultOrderSortModeForFilter(filter: string): OrderSortMode {
  if (filter === "received") return "arrival-desc";
  if (filter === "shipped") return "shipping-request-desc";
  return "default";
}

export function defaultOrderSortSettingsForList(
  workspace: "all" | "size" | "repair",
  filter: string,
): { sortMode: OrderSortMode; expressFirst: boolean } {
  const prioritizeSizeSchedule = workspace === "size" && (filter === "all" || filter === "received");
  return {
    sortMode: prioritizeSizeSchedule ? "schedule" : defaultOrderSortModeForFilter(filter),
    expressFirst: prioritizeSizeSchedule,
  };
}

export function sortDashboardOrders<T extends SortableDashboardOrder>(
  orders: readonly T[],
  options: { mode: OrderSortMode; expressFirst: boolean; today?: Date; workspace?: "all" | "size" | "repair" },
): T[] {
  const today = options.today ?? new Date();
  return orders
    .map((order, index) => ({ order, index }))
    .sort((left, right) => {
      if (
        options.workspace !== "repair"
        && options.expressFirst
        && left.order.isExpress !== right.order.isExpress
      ) {
        return left.order.isExpress ? -1 : 1;
      }
      if (options.mode === "schedule") {
        const deliveryComparison = compareDistance(
          dateDistance(left.order.requestedDeliveryDate, today),
          dateDistance(right.order.requestedDeliveryDate, today),
        );
        if (deliveryComparison !== 0) return deliveryComparison;
        const wearingComparison = compareDistance(
          dateDistance(left.order.wearingDate, today),
          dateDistance(right.order.wearingDate, today),
        );
        if (wearingComparison !== 0) return wearingComparison;
      }
      if (options.mode === "arrival-desc") {
        const comparison = compareLatestDate(
          options.workspace === "repair" ? left.order.repairInstockDate ?? "" : left.order.itemArrivalDate,
          options.workspace === "repair" ? right.order.repairInstockDate ?? "" : right.order.itemArrivalDate,
        );
        if (comparison !== 0) return comparison;
      }
      if (options.mode === "shipping-request-desc") {
        const comparison = compareLatestDate(left.order.shippingRequestDate, right.order.shippingRequestDate);
        if (comparison !== 0) return comparison;
      }
      return left.index - right.index;
    })
    .map(({ order }) => order);
}
